import MapView from '@arcgis/core/views/MapView';
import { MapViewInputTask } from '../framework/MapViewInputTask';
import { SurfaceDef } from '../model/SurfaceDef';
import { SurfaceBreakline } from '../model/SurfaceBreakline';
import { Graphic } from 'cd-eft/utils/esriTypes';
import { nearestVertex } from '@arcgis/core/geometry/geometryEngine';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import { validNextPointSymbol } from '../resources/esriSymbols';

export class DeletePointBreaklineInputTask extends MapViewInputTask<SurfaceBreakline> {
    private readonly surfaceDef: SurfaceDef;
    protected breaklineToEdit?: SurfaceBreakline;
    private feedbackLayer: GraphicsLayer = new GraphicsLayer({ title: 'deleteFeedbackLayer', listMode: 'hide' });
    private readonly tempBreaklinesLayer: GraphicsLayer = new GraphicsLayer({ title: 'tempBreaklinesLayer' });
    private pointIndexToRemove?: number;

    constructor(mapView: MapView, surfaceDef: SurfaceDef) {
        super(mapView);
        this.surfaceDef = surfaceDef;
    }

    protected onSetup(): void {
        super.onSetup();
        this.cursor = 'pointer';

        this.tempBreaklinesLayer.removeAll();

        for (const breakline of this.surfaceDef.breaklines) {
            if (breakline.groundPoints.length > 2) {
                const graphic = breakline.generateGraphic(this.mapView.spatialReference);
                graphic.symbol = new SimpleLineSymbol({ width: 3, color: 'red' });
                this.tempBreaklinesLayer.add(graphic);
            }
        }

        this.mapView.map.add(this.tempBreaklinesLayer);
        this.mapView.map.add(this.feedbackLayer);
    }

    protected onCleanup(): void {
        super.onCleanup();
        this.mapView.map.remove(this.feedbackLayer);
        this.feedbackLayer.removeAll();
        this.breaklineToEdit = undefined;

        this.mapView.map.remove(this.tempBreaklinesLayer);
        this.tempBreaklinesLayer.removeAll();
    }

    protected async onImmediateClick(e: __esri.ViewImmediateClickEvent): Promise<void> {
        super.onImmediateClick(e);
        const mapPoint = e.mapPoint;
        if (!this.breaklineToEdit) {
            // First click: select breakline
            const hitResult = await this.mapView.hitTest(e, { include: this.mapView.map.layers });
            const graphic = hitResult.results.find((r) => r.graphic?.attributes?.eft_type === 'SurfaceBreakline')
                ?.graphic;
            if (!graphic || graphic.attributes.groundPoints.length < 3) return;
            graphic.symbol = new SimpleLineSymbol({ width: 3, color: 'blue' });
            this.breaklineToEdit = graphic.attributes as SurfaceBreakline;
            this.feedbackLayer.add(graphic.clone());
            return;
        }

        if (this.pointIndexToRemove === undefined) {
            const snapResult = this.findNearestVertexOnBreakline(mapPoint);
            if (!snapResult) return;
            this.drawHighlightVertex(snapResult.point);
            const updated = this.removePointAtIndex(this.breaklineToEdit, snapResult.index);
            if (await this.surfaceDef.checkBreakLineCrosses(updated, this.mapView)) {
                this.feedbackLayer.removeAll();
                this.pointIndexToRemove = undefined;
                return;
            }
            this.surfaceDef.breaklinesSyncSet.updateItem(this.breaklineToEdit, updated, true);
            this.completeNow(updated);
        }
    }

    private drawHighlightVertex(point: __esri.Point) {
        this.feedbackLayer.removeAll();
        const graphic = new Graphic({ geometry: point, symbol: validNextPointSymbol });
        graphic.attributes = { type: 'selectedVertex' };
        this.feedbackLayer.add(graphic);
    }

    private findNearestVertexOnBreakline(mapPoint): { index: number; point: __esri.Point } | null {
        if (!this.breaklineToEdit) return null;
        const sr = this.mapView.spatialReference;
        const polyline = this.breaklineToEdit.generateGeometry(sr);
        const res = nearestVertex(polyline, mapPoint);
        if (res.distance < 3) {
            return { index: res.vertexIndex, point: res.coordinate };
        }
        return null;
    }

    private removePointAtIndex(breakline: SurfaceBreakline, index: number): SurfaceBreakline {
        const updated = new SurfaceBreakline(breakline.breaklineId);
        updated.groundPoints.push(...breakline.groundPoints);
        updated.groundPoints.splice(index, 1);
        return updated;
    }

}
